export const RECOVERY_BASE_DELAY_MS = 5_000
export const RECOVERY_MAX_DELAY_MS = 60_000
/** Each delay lies within ±20 % of its backed-off base, drawn afresh for every tick */
export const RECOVERY_JITTER = 0.2

/** Runs `run` after `ms`; returns a way to cancel it */
export type SetTimer = (run: () => void, ms: number) => () => void

/** The browser's timer; an object, so that a test can replace it under the app's own loop */
export const browserTimer: { setTimer: SetTimer } = {
  setTimer: (run, ms) => {
    const timer = setTimeout(run, ms)
    return () => clearTimeout(timer)
  },
}

/** Calls `listener` when the tab is shown or the browser comes back online; returns an unsubscribe */
export function onWake(listener: () => void): () => void {
  document.addEventListener('visibilitychange', listener)
  window.addEventListener('online', listener)
  return () => {
    document.removeEventListener('visibilitychange', listener)
    window.removeEventListener('online', listener)
  }
}

/** The delay before the `attempt`th try (from 0): doubling from the base to the cap, then jittered */
function recoveryDelayMs(attempt: number): number {
  const base = Math.min(RECOVERY_BASE_DELAY_MS * 2 ** attempt, RECOVERY_MAX_DELAY_MS)
  return Math.round(base * (1 + RECOVERY_JITTER * (2 * Math.random() - 1)))
}

const mustWait = () => document.visibilityState === 'hidden' || navigator.onLine === false

/**
 * Retries a refresh that failed without a refusal (INV-924). Security software flags fixed-period
 * retries, hidden tabs included, as a botnet and blocks the client (COR-5301): jitter on each tick,
 * backoff to a cap, no try while hidden or offline, none on waking before the deadline.
 */
export class RecoveryLoop {
  private attempt = 0
  /** When the pending or held try is due; undefined while the loop is stopped or a try runs */
  private deadline: number | undefined
  private cancelTimer: (() => void) | undefined
  private stopWaking: (() => void) | undefined

  constructor(
    private readonly setTimer: SetTimer,
    private readonly tryOnce: () => void,
  ) {}

  /** Schedules the next try unless one is pending; a failed try calls this again, one step later */
  schedule = (): void => {
    if (this.cancelTimer) return
    const delay = recoveryDelayMs(this.attempt)
    this.attempt += 1
    this.deadline = Date.now() + delay
    this.stopWaking ??= onWake(this.wake)
    this.cancelTimer = this.setTimer(this.tick, delay)
  }

  /** Ends the loop: on recovery, or once the session ended or the user signed out */
  stop = (): void => {
    this.cancelTimer?.()
    this.stopWaking?.()
    this.cancelTimer = undefined
    this.stopWaking = undefined
    this.deadline = undefined
    this.attempt = 0
  }

  private tick = () => {
    this.cancelTimer = undefined
    if (mustWait()) return // held: wake() tries once the tab is shown and the browser online
    this.tryNow()
  }

  private wake = () => {
    const due = this.deadline !== undefined && Date.now() >= this.deadline
    if (this.cancelTimer || !due || mustWait()) return
    this.tryNow()
  }

  private tryNow() {
    this.deadline = undefined
    this.tryOnce()
  }
}
